import { useEffect, useState } from "react";
import { Copy, Pencil, Trash2, Plus, Layers, CornerDownRight, RotateCcw, ArrowUpDown } from "lucide-react";
import { Button } from "./ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { PieceDrawing } from "./PieceDrawing";
import { THICK_BADGE, edgesText, machiningText, edgeMeters, tamponPieces } from "../lib/pieces";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border border-[#2D333F] bg-[#181B21] px-4 py-3">
    <p className="font-mono text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</p>
    <p data-testid={testId} className="font-heading text-2xl font-bold mt-0.5">{value}</p>
  </div>
);

const NumCell = ({ value, field, onSave, testId, className = "" }) => {
  const [v, setV] = useState(String(value));
  useEffect(() => { setV(String(value)); }, [value]);
  const commit = () => {
    const n = field === "qty" ? parseInt(v, 10) : parseFloat(String(v).replace(",", "."));
    if (!n || n <= 0 || n === +value) { setV(String(value)); return; }
    onSave(field, n);
  };
  return (
    <input data-testid={testId} value={v} inputMode="decimal" onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setV(String(value)); e.currentTarget.blur(); } }}
      className={`w-16 rounded border border-transparent bg-transparent px-1 py-0.5 font-mono hover:border-[#2D333F] focus:border-[#E67E22] focus:bg-[#0F1115] focus:outline-none ${className}`} />
  );
};

const Cells = ({ p, testId, onSave, i }) => (
  <>
    <td className="px-1 py-1"><PieceDrawing piece={p} showLabels={false} className="w-14 h-10" testId={testId} /></td>
    <td className="px-3 py-2">
      <p className="font-medium">{p.name}</p>
      {p.module && <p className="text-[11px] text-[#CA7C38]">{p.module}</p>}
    </td>
    {onSave ? <>
      <td className="px-2 py-2"><NumCell value={p.length} field="length" onSave={onSave} testId={`inline-length-${i}`} /></td>
      <td className="px-2 py-2"><NumCell value={p.width} field="width" onSave={onSave} testId={`inline-width-${i}`} /></td>
      <td className="px-2 py-2"><NumCell value={p.qty} field="qty" onSave={onSave} testId={`inline-qty-${i}`} className="w-12 font-bold text-[#F39C12]" /></td>
    </> : <>
      <td className="px-3 py-2 font-mono">{p.length}</td>
      <td className="px-3 py-2 font-mono">{p.width}</td>
      <td className="px-3 py-2 font-mono font-bold text-[#F39C12]">{p.qty}</td>
    </>}
    <td className="px-3 py-2"><span className={`inline-block rounded border px-1.5 py-0.5 font-mono text-[11px] ${THICK_BADGE[p.thickness]}`}>{p.thickness}mm</span></td>
    <td className="px-3 py-2 text-[#D1D5DB] whitespace-nowrap">{p.mdf_color || "—"}</td>
    <td className="px-3 py-2 whitespace-nowrap">
      <p className="font-mono text-xs">{edgesText(p)}</p>
      <p className="text-[11px] text-[#6B7280]">{p.edge_color}</p>
    </td>
    <td className="px-3 py-2 text-xs text-[#00E5FF] whitespace-nowrap">{machiningText(p) || <span className="text-[#4B5563]">—</span>}{p.grain && <span className="block text-[#CA7C38]">Segue veio</span>}</td>
  </>
);

export const PiecesTable = ({ pieces, onAdd, onEdit, onDuplicate, onDelete, onClear, onRestore, restoreCount, readOnly, onInline, flashId }) => {
  const [confirm, setConfirm] = useState(false);
  const [newestFirst, setNewestFirst] = useState(() => localStorage.getItem("pieces_newest_first") === "1");
  const flip = () => setNewestFirst((o) => { localStorage.setItem("pieces_newest_first", o ? "0" : "1"); return !o; });
  const rows = pieces.map((p, i) => [p, i]);
  if (newestFirst) rows.reverse();
  const all = pieces.flatMap((p) => [p, ...tamponPieces(p)]);
  const qty = all.reduce((s, p) => s + (+p.qty || 0), 0);
  const area = all.reduce((s, p) => s + p.length * p.width * p.qty, 0) / 1e6;
  const meters = Object.values(edgeMeters(all)).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Tipos de peça" value={all.length} testId="stat-piece-types" />
        <Stat label="Total de peças" value={qty} testId="stat-piece-total" />
        <Stat label="Área total" value={`${area.toFixed(2)} m²`} testId="stat-area" />
        <Stat label="Fita de borda" value={`${meters.toFixed(1)} m`} testId="stat-edge-meters" />
      </div>
      {!readOnly && <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="add-piece-btn" onClick={onAdd} className="bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Adicionar peça
        </Button>
        {pieces.length > 0 && (
          <Button data-testid="clear-pieces-btn" variant="ghost" onClick={() => setConfirm(true)} className="text-[#9CA3AF] hover:text-red-400">
            <Trash2 className="w-4 h-4 mr-1" /> Limpar lista
          </Button>
        )}
        {pieces.length > 1 && (
          <Button data-testid="invert-pieces-btn" variant="ghost" onClick={flip} className={newestFirst ? "text-[#E67E22]" : "text-[#9CA3AF]"}>
            <ArrowUpDown className="w-4 h-4 mr-1" /> {newestFirst ? "Mais recentes no topo" : "Mais recentes no final"}
          </Button>
        )}
        {restoreCount > 0 && (
          <Button data-testid="restore-pieces-btn" variant="outline" onClick={onRestore} className="border-[#E67E22] text-[#E67E22] bg-transparent hover:bg-[#21252F]">
            <RotateCcw className="w-4 h-4 mr-1" /> Restaurar lista limpa ({restoreCount} tipos de peça)
          </Button>
        )}
      </div>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="clear-pieces-dialog" className="bg-[#181B21] border-[#2D333F] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Limpar a lista de peças?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              Os {pieces.length} tipos de peça serão removidos deste ambiente. Você poderá restaurar pelo botão "Restaurar lista limpa".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-pieces-cancel" className="bg-transparent border-[#2D333F] text-white hover:bg-[#21252F]">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="clear-pieces-confirm" onClick={onClear} className="bg-red-600 hover:bg-red-700 text-white">Sim, limpar lista</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {pieces.length === 0 ? (
        <div data-testid="pieces-empty" className="rounded-xl border border-dashed border-[#2D333F] p-12 text-center">
          <Layers className="w-10 h-10 mx-auto text-[#4B5563]" />
          <p className="mt-3 font-heading text-lg">Nenhuma peça ainda</p>
          <p className="text-sm text-[#6B7280]">Adicione peças manualmente ou use um módulo pronto.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-[#2D333F] overflow-x-auto">
          <table data-testid="pieces-table" className="w-full text-sm">
            <thead className="bg-[#13151B] text-[#9CA3AF] font-mono text-[11px] uppercase tracking-wider">
              <tr>
                {["#", "", "Peça", "Comp.", "Larg.", "Qtd", "Esp.", "MDF", "Fita", "Usinagem", ""].map((h, i) => (
                  <th key={i} className="text-left px-3 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([p, i]) => [
                <tr key={p.id} data-testid={`piece-row-${i}`} className={`border-t border-[#2D333F] hover:bg-[#1C2028] transition-colors ${flashId === p.id ? "row-flash" : ""}`}>
                  <td className="px-3 py-2 font-mono text-[#6B7280]">{i + 1}</td>
                  <Cells p={p} testId={`piece-thumb-${i}`} i={i} onSave={!readOnly && onInline ? (f, val) => onInline(i, f, val) : null} />
                  <td className="px-2 py-2 whitespace-nowrap">
                    {!readOnly && <>
                    <button data-testid={`edit-piece-${i}`} onClick={() => onEdit(i)} className="p-1.5 rounded hover:bg-[#2D333F]" title="Editar"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`duplicate-piece-${i}`} onClick={() => onDuplicate(i)} className="p-1.5 rounded hover:bg-[#2D333F]" title="Duplicar"><Copy className="w-4 h-4" /></button>
                    <button data-testid={`delete-piece-${i}`} onClick={() => onDelete(i)} className="p-1.5 rounded hover:bg-red-950 text-red-400" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                    </>}
                  </td>
                </tr>,
                ...tamponPieces(p).map((d, j) => (
                  <tr key={d.id} data-testid={`tampon-row-${i}-${j}`} className="bg-[#13151B] text-[#D1D5DB]">
                    <td className="px-3 py-2 text-[#CA7C38]"><CornerDownRight className="w-4 h-4" /></td>
                    <Cells p={d} testId={`tampon-thumb-${i}-${j}`} />
                    <td className="px-2 py-2 text-[10px] text-[#6B7280] whitespace-nowrap">gerada pelo<br />tamponamento</td>
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
